import { getFileExtension, getFileNameNoExt } from ".";
import { 
    convertFileToPDF, 
    createFolder, 
    deleteFolderAndContents 
} from "../../apiCallers/graph/file/folder.handler";
import { 
    uploadBlobFileToDest, 
    uploadFileDirectly, 
    uploadFileToRoot 
} from "../../apiCallers/graph/file/upload_file";
import { MS_GRAPH_EXT_TO_PDF, IMAGE_EXT_TO_PDF } from "../../config/general_vars";
import { PDFDocument } from "pdf-lib";
import sharepoint from "../../apiCallers/promise_handler_db/sharepoint";

const fileDirectUpload = async (file_list = [], case_id, folder) => {
    console.log("Uploading files directly", file_list);
    const results = { success: [], failed: [] };
    
    await Promise.all(file_list.map(async (file) => {
        try {
            await uploadFileDirectly(file, case_id, folder.id);
            results.success.push(file.name);
        } catch (error) {
            console.error("Error uploading file directly:", file.name, error);
            results.failed.push(file.name);
        }
    }));
    
    return results;
};
const fileDownloadFinalization = async (file_list = [], case_id, folder) => {
    const files = file_list
        .filter(f => f && f.id && f.parentReference && f.parentReference.driveId)
        .map(f => ({
            driveId: f.parentReference.driveId,
            itemId: f.id,
            name: f.name,
        }));

    if (files.length === 0) {
        return { success: [], failed: [] };
    }

    let resp;
    try {
        resp = await sharepoint.finalizeFilesToSpis(case_id, folder.id, files);
    } catch (error) {
        console.error("finalizeFilesToSpis failed", error);
        return { success: [], failed: files.map(f => f.name) };
    }
    const results = (resp && resp.data) || {};
    return {
        success: results.success || [],
        failed: results.failed || [],
    };
};

const fileFinalization = async (file_list = [], case_id, folder) => {
    const folder_upload_id = folder.id;
    const results = { success: [], failed: [] };

    const pdf_files = file_list.filter(p => getFileExtension(p.name) === "pdf");
    const ms_graph_convertable_files = file_list.filter(p => MS_GRAPH_EXT_TO_PDF.includes(getFileExtension(p.name)));
    const image_convertable_files = file_list.filter(p => IMAGE_EXT_TO_PDF.includes(getFileExtension(p.name)));

    // Files no branch below handles must still be reported, otherwise the UI shows success for a file that was never uploaded.
    const handled = [...pdf_files, ...ms_graph_convertable_files, ...image_convertable_files];
    file_list.filter(p => !handled.includes(p)).forEach(p => results.failed.push(p.name));
    
    // Upload PDFs directly
    await Promise.all(pdf_files.map(async (file) => {
        try {
            await uploadFileDirectly(file, case_id, folder_upload_id);
            results.success.push(file.name);
        } catch (error) {
            console.error("Error uploading PDF file:", file.name, error);
            results.failed.push(file.name);
        }
    }));

    // Convert and upload MS Graph files
    if (ms_graph_convertable_files.length > 0) {
        const msGraphResults = await msGraphExportPDFSequence(ms_graph_convertable_files, folder);
        results.success.push(...msGraphResults.success);
        results.failed.push(...msGraphResults.failed);
    }
    
    // Convert and upload image files to PDF
    await Promise.all(image_convertable_files.map(async (file) => {
        try {
            const converted_file = await imageToPDF(file);
            const file_pdf_name = getFileNameNoExt(file.name) + ".pdf";
            await uploadBlobFileToDest(converted_file, file_pdf_name, folder.parentReference.driveId, folder.id);
            results.success.push(file_pdf_name);
        } catch (error) {
            console.error("Error converting image to PDF:", file.name, error);
            results.failed.push(file.name);
        }
    }));
    
    console.log("Upload results:", results);
    return results;
};

export async function imageToPDF(imageFile) {
    if (!imageFile) throw new Error("No image file provided");

    const pdfDoc = await PDFDocument.create();
    const imageBytes = await imageFile.contentBytes;

    let embeddedImage;
    if (imageFile.contentType === "image/png") {
        embeddedImage = await pdfDoc.embedPng(imageBytes);
    } else {
        embeddedImage = await pdfDoc.embedJpg(imageBytes);
    }

    const page = pdfDoc.addPage([embeddedImage.width, embeddedImage.height]);
    page.drawImage(embeddedImage, { x: 0, y: 0, width: embeddedImage.width, height: embeddedImage.height });

    const pdfBytes = await pdfDoc.save();
    return new Blob([pdfBytes], { type: "application/pdf" });
}

const msGraphExportPDFSequence = async (file_list, folder) => {
    console.log("Converting MS Graph files to PDF", file_list, folder);
    const results = { success: [], failed: [] };
    
    let folder_created_obj;
    try {
        const temp_folder_name = folder.id + "-" + Date.now();
        folder_created_obj = await createFolder(temp_folder_name);
    } catch (error) {
        console.error("Error creating temp folder", error);
        results.failed.push(...file_list.map(file => file.name));
        return results;
    }

    await Promise.all(file_list.map(async (file) => {
        try {
            const uploaded_file = await uploadFileToRoot(file, folder_created_obj.id);
            console.log("File uploaded to temp folder", uploaded_file);
            const converted_file = await convertFileToPDF(uploaded_file.parentReference.driveId, uploaded_file.id);
            const file_pdf_name = getFileNameNoExt(file.name) + ".pdf";
            await uploadBlobFileToDest(converted_file, file_pdf_name, folder.parentReference.driveId, folder.id);
            results.success.push(file_pdf_name);
        } catch (error) {
            console.error("Error converting MS Graph file to PDF:", file.name, error);
            results.failed.push(file.name);
        }
    }));

    try {
        const delete_path = `/me/drive/items/${folder_created_obj.id}`;
        await deleteFolderAndContents(delete_path);
    } catch (error) {
        console.error("Error deleting temp folder", error);
    }
    
    return results;
};

export { fileFinalization, fileDirectUpload,fileDownloadFinalization };
